import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { useUser } from "../../context/UserContext";
import { AuthService } from "../../service/auth_service";
import { endSession, redirectToLogin } from "../../utils/session";

const authService = new AuthService();

// Telas sem login: lá não há usuário para cobrar a data.
const ROTAS_PUBLICAS = ["/", "/rastreabilidade"];

const hojeISO = () => {
	const d = new Date();
	const p = (n) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Pede a data de nascimento a quem foi cadastrado antes de ela ser
 * obrigatória (`precisaDataNascimento` no perfil).
 *
 * Fica montado no App, ao lado do `TermosUsoCheck`, para aparecer em qualquer
 * tela logada. Não fecha sem informar a data. Menor de 18: o backend grava a
 * data (barra os próximos logins) e responde 403; aqui avisamos e encerramos
 * a sessão.
 */
export default function DataNascimentoCheck() {
	const { user, authStatus, setUser } = useUser();
	const { pathname } = useLocation();
	const [data, setData] = useState("");
	const [erro, setErro] = useState("");
	const [bloqueio, setBloqueio] = useState("");
	const [salvando, setSalvando] = useState(false);

	const logado = authStatus === "authenticated" && !ROTAS_PUBLICAS.includes(pathname);
	const aberto = (logado && user?.precisaDataNascimento === true) || !!bloqueio;

	const sair = () => {
		endSession().finally(redirectToLogin);
	};

	const handleSalvar = async () => {
		if (!data) {
			setErro("Informe a data de nascimento.");
			return;
		}
		setErro("");
		setSalvando(true);
		try {
			await authService.informarDataNascimento(data);
			setUser((atual) => (atual ? { ...atual, precisaDataNascimento: false } : atual));
		} catch (error) {
			if (error.status === 403) setBloqueio(error.message);
			else setErro(error.message);
		} finally {
			setSalvando(false);
		}
	};

	return (
		<Dialog open={aberto} disableEscapeKeyDown maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
				{bloqueio ? "Acesso não permitido" : "Informe sua data de nascimento"}
			</DialogTitle>

			<DialogContent dividers sx={{ p: 3 }}>
				{bloqueio ? (
					<Alert severity="error">{bloqueio}</Alert>
				) : (
					<>
						<Typography variant="body2" sx={{ mb: 2 }}>
							Para continuar a utilizar a plataforma, precisamos da sua data de nascimento.
						</Typography>
						<TextField
							fullWidth
							autoFocus
							type="date"
							label="Data de nascimento"
							value={data}
							onChange={(e) => setData(e.target.value)}
							InputLabelProps={{ shrink: true }}
							inputProps={{ max: hojeISO(), min: "1900-01-01" }}
							error={!!erro}
							helperText={erro || " "}
							sx={{
								"& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#1a9e8f" },
							}}
						/>
					</>
				)}
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				{bloqueio ? (
					<Button variant="contained" color="error" onClick={sair} fullWidth sx={{ py: 1.5, fontWeight: "bold" }}>
						Sair
					</Button>
				) : (
					<Button
						variant="contained"
						onClick={handleSalvar}
						disabled={salvando}
						fullWidth
						sx={{
							py: 1.5,
							fontWeight: "bold",
							bgcolor: "#1a9e8f",
							"&:hover": { bgcolor: "#158a7d" },
						}}
					>
						{salvando ? "Salvando..." : "Confirmar"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
